function renderInsuranceRow(
  policy,
  rows,
  propertyHrId,
  buildings,
  buildingsLoaded
) {
  const href = escapeHtml(luxInsuranceUrl(propertyHrId, policy.id))
  const ended = policy.status === 'ended'
  const unscoped = !policy.assignments.length
  const types = [...policy.types].sort(
    (a, b) => Number(b === 'building') - Number(a === 'building')
  )
  const label =
    types.map((kind) => LUX_INSURANCE_KIND_LABELS[kind] || kind)[0] ||
    'Versicherung'
  const reference = policy.policyNumber || label
  const others = rows.filter(
    (row) => row.id !== policy.id && row.status !== 'ended'
  )
  const isBuilding = types[0] === 'building'
  const buildingScope = insuranceCoveredBuildings(
    policy,
    'building',
    'standalone',
    buildings
  )
  // A risk on its own policy is "separate" when a Wohngebäude policy covers the same buildings.
  const separate =
    !ended &&
    LUX_INSURANCE_SEPARABLE_RISKS.includes(types[0]) &&
    others.some((row) =>
      insuranceOverlaps(
        insuranceCoveredBuildings(row, 'building', 'standalone', buildings),
        insuranceCoveredBuildings(policy, types[0], 'standalone', buildings)
      )
    )
  const chips = []
  if (unscoped && !ended) {
    chips.push(
      '<span class="lux-ins-chip lux-ins-chip-none">Keine Deckung aktiv</span>'
    )
  }
  for (const kind of types.slice(1)) {
    chips.push(
      `<span class="lux-ins-chip">${escapeHtml(LUX_INSURANCE_KIND_LABELS[kind] || kind)}</span>`
    )
  }
  for (const [kind, riskLabel] of Object.entries(LUX_INSURANCE_RISK_LABELS)) {
    if (
      policy.assignments.some(
        (row) => row.coverageKind === kind && row.mode === 'included'
      )
    ) {
      chips.push(
        `<span class="lux-ins-chip lux-ins-chip-included" data-insurance-risk="${kind}">${riskLabel} [Ja]</span>`
      )
      continue
    }
    const carrier =
      isBuilding &&
      !ended &&
      LUX_INSURANCE_SEPARABLE_RISKS.includes(kind) &&
      buildingScope.size &&
      others.find(
        (row) =>
          row.types.includes(kind) &&
          insuranceOverlaps(
            buildingScope,
            insuranceCoveredBuildings(row, kind, 'standalone', buildings)
          )
      )
    if (carrier) {
      const carrierLabel =
        LUX_INSURANCE_KIND_LABELS[carrier.types[0]] ||
        carrier.types[0] ||
        'Versicherung'
      const carrierTitle = `Versichert über ${carrierLabel} · ${carrier.policyNumber || `#${carrier.id}`}`
      chips.push(
        `<span class="lux-ins-chip lux-ins-chip-separate" data-insurance-risk="${kind}" title="${escapeHtml(carrierTitle)}">${riskLabel}: separate Police</span>`
      )
    }
  }
  const status = ended
    ? '<span class="lux-ins-badge lux-ins-badge-ended" data-insurance-ended>Beendet</span>'
    : unscoped
      ? '<span class="lux-ins-badge lux-ins-badge-incomplete" data-insurance-incomplete>Unvollständig</span>'
      : insurancePlanned(policy)
        ? '<span class="lux-ins-badge lux-ins-badge-planned" data-insurance-planned>Geplant</span>'
        : '<span class="lux-ins-badge lux-ins-badge-active">Aktiv</span>'
  const duplicates = insuranceDuplicatePolicies(policy, rows, buildings)
  const duplicateTitle = `Gleiche Versicherungsart im selben Zeitraum wie: ${duplicates
    .map(
      (row) =>
        `${LUX_INSURANCE_KIND_LABELS[row.types[0]] || row.types[0] || 'Versicherung'} · ${row.policyNumber || `#${row.id}`}`
    )
    .join(', ')}`
  const duplicateBadge = duplicates.length
    ? `<span class="lux-ins-badge lux-ins-badge-duplicate" data-insurance-duplicate title="${escapeHtml(duplicateTitle)}">Doppelt versichert</span>`
    : ''
  const insuredSum = insuranceEuro(policy.insuredSum, 0)
  const nameCell = `<td><div class="lux-ins-title"><span class="lux-ins-name">${escapeHtml(label)}</span>${status}${duplicateBadge}${separate ? '<span class="lux-ins-badge lux-ins-badge-separate" data-insurance-separate>Separate Police</span>' : ''}</div>
    <div class="lux-ins-policy-number">${
      policy.policyNumber
        ? `<code class="lux-ins-pill">${escapeHtml(policy.policyNumber)}</code><button type="button" class="lux-ins-copy" data-copy-insurance title="Nummer kopieren" aria-label="Policennummer kopieren">${insuranceOverviewIcon('copy')}</button>`
        : '<span>Keine Policennummer</span>'
    }${insuredSum ? `<span>· Deckung: ${escapeHtml(insuredSum)}</span>` : ''}</div>
    ${chips.length ? `<div class="lux-ins-chips">${chips.join('')}</div>` : ''}
    ${ended && policy.termEndDate ? `<div class="lux-ins-note">Beendet zum ${isoDateToGermanDisplay(policy.termEndDate)}</div>` : ''}</td>`

  const coverageText = insuranceCoverageLabel(policy, buildings)
  const ids = [...new Set(policy.assignments.map((row) => row.buildingId))]
  const buildingName = (id) =>
    buildings.find((building) => building.id === id)?.name || `Gebäude ${id}`
  let scope
  if (unscoped) {
    scope = `<span class="lux-ins-scope lux-ins-scope-pill lux-ins-unscoped" data-insurance-unscoped>${insuranceOverviewIcon('warning')}Nicht zugeordnet${buildingsLoaded ? ' (0 Gebäude)' : ''}</span><div class="lux-ins-alert">Bitte zuordnen oder löschen</div>`
  } else if (ids.length === 1 && ids[0] === null) {
    scope = `<span class="lux-ins-scope">${insuranceOverviewIcon('buildings')}Gesamtes Objekt</span>${
      ended
        ? '<div class="lux-ins-sub">Archiviert</div>'
        : buildingsLoaded && buildings.length
          ? `<div class="lux-ins-sub">${buildings.length === 1 ? 'Das Gebäude' : `Alle ${buildings.length} Gebäude`} zugeordnet</div><div class="lux-ins-buildings">${buildings
              .slice(0, 4)
              .map(
                (building) =>
                  `<span class="lux-ins-building">${escapeHtml(building.name || `Gebäude ${building.id}`)}</span>`
              )
              .join(
                ''
              )}${buildings.length > 4 ? `<span class="lux-ins-building">+${buildings.length - 4}</span>` : ''}</div>`
          : ''
    }`
  } else if (ids.includes(null)) {
    scope = `<span class="lux-ins-scope">${insuranceOverviewIcon('buildings')}Unterschiedliche Zuordnungen</span><div class="lux-ins-sub">${escapeHtml(coverageText)}</div>`
  } else if (ids.length === 1) {
    const building = buildings.find((item) => item.id === ids[0])
    scope = `<span class="lux-ins-scope lux-ins-scope-pill lux-ins-scope-single">${insuranceOverviewIcon('home')}Einzelnes Gebäude</span><div class="lux-ins-sub-strong">${escapeHtml(buildingName(ids[0]))}${building?.hrId ? ` <span class="lux-ins-muted">(${escapeHtml(building.hrId)})</span>` : ''}</div>`
  } else {
    scope = `<span class="lux-ins-scope lux-ins-scope-pill">${insuranceOverviewIcon('buildings')}Ausgewählte Gebäude (${ids.length})</span><div class="lux-ins-sub-strong">${ids.map((id) => escapeHtml(buildingName(id))).join(', ')}</div>`
  }
  const scopeCell = `<td title="${escapeHtml(coverageText)}">${scope}<span class="sr-only">${escapeHtml(coverageText)}</span></td>`

  const insurerName = insuranceContactName(policy.insurer)
  const brokerName = insuranceContactName(policy.brokerContact)
  const deductible = insuranceEuro(policy.deductible, 2)
  const insurerCell = `<td><div class="lux-ins-contact">${insurerName ? `${insuranceOverviewIcon('building')}${escapeHtml(insurerName)}` : '<span class="lux-ins-muted">Kein Versicherer hinterlegt</span>'}</div>
    <div class="lux-ins-broker">${
      brokerName
        ? `${insuranceOverviewIcon('badge')}<span>${escapeHtml(brokerName)}</span><span class="lux-ins-broker-tag">Makler</span>`
        : insurerName
          ? `${insuranceOverviewIcon('user')}<span>Direktvertrag (ohne Makler)</span>`
          : '<em class="lux-ins-muted">Kein Makler hinterlegt</em>'
    }</div>
    ${deductible ? `<div class="lux-ins-sub">Selbstbehalt: ${escapeHtml(deductible)}</div>` : ''}</td>`

  const interval =
    { YEARLY: 'jährlich', QUARTERLY: 'vierteljährlich', MONTHLY: 'monatlich' }[
      policy.paymentInterval
    ] || ''
  const due = policy.dueDate
    ? isoDateToGermanDisplay(policy.dueDate).slice(0, 6)
    : '--.--.'
  const notice =
    policy.noticePeriodMonths != null
      ? `<div class="lux-ins-sub">Kündigungsfrist: ${escapeHtml(insuranceCount(policy.noticePeriodMonths, 'Monat', 'Monate'))}</div>`
      : ''
  const term = policy.termStartDate
    ? `${isoDateToGermanDisplay(policy.termStartDate)} – ${policy.termEndDate ? isoDateToGermanDisplay(policy.termEndDate) : 'unbefristet'}`
    : policy.termEndDate
      ? `bis ${isoDateToGermanDisplay(policy.termEndDate)}`
      : 'Laufzeit unvollständig'
  const dueCell = ended
    ? `<td><div class="lux-ins-due">${policy.termEndDate ? `Ablauf: ${isoDateToGermanDisplay(policy.termEndDate)}` : 'Abgelaufen'}</div><div class="lux-ins-sub">Vertrag beendet</div></td>`
    : `<td><div class="lux-ins-due">${due}${interval ? ` ${interval}` : ''}</div><div class="lux-ins-sub">${term}</div>${notice}</td>`

  const documentsCell = `<td class="lux-ins-center"><a class="lux-ins-documents" href="${href}#insurance-documents" aria-label="Dokumente zu ${escapeHtml(reference)} öffnen">${insuranceOverviewIcon('folder')}${policy.documentCount == null ? 'Öffnen' : `${escapeHtml(policy.documentCount)} Dok.`}</a></td>`
  const actionsCell = `<td class="lux-ins-right"><div class="lux-ins-actions" data-insurance-actions>
    <a class="lux-ins-action" data-edit-insurance href="${href}" title="Bearbeiten" aria-label="${escapeHtml(`Versicherung ${reference} bearbeiten`)}">${LUX_ROW_ACTION_ICONS.edit}</a>
    <button type="button" class="lux-ins-action" data-insurance-status-toggle="${ended ? 'active' : 'ended'}" title="${ended ? 'Police reaktivieren' : 'Police beenden'}" aria-label="${escapeHtml(`Versicherung ${reference} ${ended ? 'reaktivieren' : 'beenden'}`)}">${LUX_ROW_ACTION_ICONS[ended ? 'play' : 'pause']}</button>
    <button type="button" class="lux-ins-action" data-delete-insurance title="Löschen" aria-label="${escapeHtml(`Versicherung ${reference} löschen`)}">${LUX_ROW_ACTION_ICONS.delete}</button>
  </div></td>`
  return `<tr class="impowerx-ta-row${ended ? ' lux-ins-ended' : unscoped ? ' lux-ins-incomplete' : ''}" data-insurance-id="${escapeHtml(policy.id)}">${nameCell}${scopeCell}${insurerCell}${dueCell}${documentsCell}${actionsCell}</tr>`
}

function insuranceBuildingSummary(
  rows,
  buildings,
  buildingId,
  buildingsLoaded
) {
  if (!buildingsLoaded) return '<span>Gebäudezuordnung nicht verfügbar</span>'
  const scoped = buildingId
    ? buildings.filter((building) => String(building.id) === buildingId)
    : buildings
  if (!scoped.length) return ''
  const covered = new Set(
    rows
      .filter((row) => row.status !== 'ended')
      .flatMap((row) => [
        ...insuranceCoveredBuildings(row, 'building', 'standalone', buildings),
      ])
  )
  const missing = scoped.filter((building) => !covered.has(building.id)).length
  if (!missing) {
    const text =
      scoped.length === 1
        ? 'Gebäude mit Wohngebäudeversicherung'
        : `Alle ${scoped.length} Gebäude mit Wohngebäudeversicherung`
    return `<span class="lux-ins-summary-ok" data-insurance-buildings-covered>${text}</span>`
  }
  return `<span class="lux-ins-summary-warn" data-insurance-buildings-missing>${missing} von ${insuranceCount(scoped.length, 'Gebäude', 'Gebäuden')} ohne Wohngebäudeversicherung</span>`
}

function renderInsuranceTable(
  rows,
  propertyHrId,
  buildings,
  {
    all = rows,
    total = all.length,
    buildingId = '',
    buildingsLoaded = buildings.length > 0,
    loading = false,
    error = null,
    page = 1,
    sort = null,
  } = {}
) {
  // Default order: active, then incomplete, then ended; a column sort replaces it.
  rows = sort
    ? sortInsuranceRows(rows, sort, buildings)
    : [...rows].sort((a, b) => insuranceRowRank(a) - insuranceRowRank(b))
  const pages = Math.max(1, Math.ceil(rows.length / LUX_INSURANCE_PAGE_SIZE))
  page = Math.min(Math.max(1, page), pages)
  const visible = rows.slice(
    (page - 1) * LUX_INSURANCE_PAGE_SIZE,
    page * LUX_INSURANCE_PAGE_SIZE
  )
  const endedCount = all.filter((row) => row.status === 'ended').length
  const plannedCount = all.filter((row) => insurancePlanned(row)).length
  const active = all.length - endedCount - plannedCount
  const dot = '<span class="lux-ins-summary-dot" aria-hidden="true">·</span>'
  // Coverage is a property fact, so search and Sparte filters must not hide it.
  const buildingSummary = insuranceBuildingSummary(
    all,
    buildings,
    buildingId,
    buildingsLoaded
  )
  const stats =
    rows.length === total
      ? [
          `<span><span data-insurance-count>${active}</span> ${active === 1 ? 'aktive Police' : 'aktive Policen'}</span>`,
          plannedCount
            ? `<span data-insurance-planned-count>${insuranceCount(plannedCount, 'geplante Police', 'geplante Policen')}</span>`
            : '',
          endedCount
            ? `<span>${insuranceCount(endedCount, 'beendete Police', 'beendete Policen')}</span>`
            : '',
        ]
      : [
          `<span>${rows.length} von <span data-insurance-count>${total}</span> Policen</span>`,
        ]
  const pageButton = (target, content, labelText, current = false) =>
    `<button type="button" data-insurance-page="${target}" aria-label="${labelText}"${current ? ' aria-current="page"' : ''}${target < 1 || target > pages ? ' disabled' : ''}>${content}</button>`
  const footer = `<footer class="lux-ins-summary"><div class="lux-ins-summary-stats">${insuranceOverviewIcon('check')}${[...stats, buildingSummary].filter(Boolean).join(dot)}</div>
    <nav class="lux-ins-pager" aria-label="Seiten"><span>Seite ${page} von ${pages} (${insuranceCount(rows.length, 'Eintrag', 'Einträge')})</span>${pageButton(page - 1, insuranceOverviewIcon('prev'), 'Vorherige Seite')}${pageButton(page, page, `Seite ${page}`, true)}${pageButton(page + 1, insuranceOverviewIcon('next'), 'Nächste Seite')}</nav></footer>`
  return `<div class="lux-ins-table-card"><div class="lux-ins-table-scroll">
    <table><caption class="sr-only">Versicherungsübersicht</caption>
      <colgroup><col style="width:27%"><col style="width:18%"><col style="width:19%"><col style="width:16%"><col style="width:100px"><col style="width:116px"></colgroup>
      <thead><tr>${LUX_INSURANCE_SORT_COLUMNS.map((column, index) => {
        const dir = sort && sort.key === column.key ? sort.dir : null
        const icon = insuranceOverviewIcon(
          dir === 'asc' ? 'sortAsc' : dir === 'desc' ? 'sortDesc' : 'sortNone'
        )
        return `<th scope="col" class="${index === 4 ? 'lux-ins-center' : ''}" data-insurance-sort="${column.key}" aria-sort="${dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}" role="button" tabindex="0" title="Nach „${column.label}“ sortieren"><span class="lux-ins-sort">${column.label}${icon}</span></th>`
      }).join(
        ''
      )}<th scope="col" class="lux-ins-right">Aktionen</th></tr></thead>
      <tbody>${
        loading
          ? '<tr><td class="lux-ins-loading" colspan="6" role="status"><span class="lux-ins-spinner" aria-hidden="true"></span>Versicherungen werden geladen…</td></tr>'
          : error
            ? `<tr><td colspan="6" role="alert">${escapeHtml(error)}</td></tr>`
            : visible.length
              ? visible
                  .map((policy) =>
                    renderInsuranceRow(
                      policy,
                      all,
                      propertyHrId,
                      buildings,
                      buildingsLoaded
                    )
                  )
                  .join('')
              : '<tr><td colspan="6" data-insurance-empty>Für dieses Objekt sind noch keine Versicherungen erfasst. Über „Versicherung hinzufügen“ legen Sie die erste Police an.</td></tr>'
      }</tbody>
    </table></div>${loading || error ? '' : footer}</div>`
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { renderInsuranceTable }
}
